// @ts-nocheck
import { BomLeadTimeResponseModel } from "@store/Framework/Types/ValveConfiguratorApiModels";
import { postValveBomLeadTime } from "@store/Framework/Services/ValveConfiguratorService";
import { useEffect, useRef, useState } from "react";

export interface BomLeadTimeState {
    loading: boolean;
    error: string | null;
    result?: BomLeadTimeResponseModel;
    /** Ready-to-render caption, e.g. "Estimated shipping: Sep 12, 2026" or "Estimated shipping: Up to 18 weeks". */
    caption?: string;
}

export interface UseBomLeadTimeOptions {
    unitPartNumber: string;
    bomPartNumbers: string[];
    bomQuantities?: { [partNumber: string]: number };
    qtyOrdered?: number;
    isQuoteToOrder?: boolean;
    /** Skip the request entirely (e.g. while pricing is still loading). */
    skip?: boolean;
}

const requestKey = (o: UseBomLeadTimeOptions) =>
    [
        (o.unitPartNumber || "").trim().toUpperCase(),
        [...new Set(o.bomPartNumbers.map(p => p.trim().toUpperCase()).filter(Boolean))].sort().join(","),
        o.qtyOrdered ?? 1,
        o.isQuoteToOrder ? "qto" : "std",
    ].join("|");

const inflight = new Map<string, Promise<BomLeadTimeResponseModel>>();

function fetchDeduped(key: string, body: UseBomLeadTimeOptions): Promise<BomLeadTimeResponseModel> {
    const existing = inflight.get(key);
    if (existing) {
        return existing;
    }
    const p = postValveBomLeadTime({
        unitPartNumber: body.unitPartNumber,
        bomPartNumbers: body.bomPartNumbers,
        bomQuantities: body.bomQuantities,
        qtyOrdered: body.qtyOrdered ?? 1,
        isQuoteToOrder: !!body.isQuoteToOrder,
    });
    inflight.set(key, p);
    p.finally(() => inflight.delete(key));
    return p;
}

export function buildLeadTimeCaption(result: BomLeadTimeResponseModel | undefined): string | undefined {
    if (!result) {
        return undefined;
    }
    if (result.source === "Fallback") {
        // Same per-product-line "RealTimeInventory_LeadTime" copy real-time inventory uses;
        // null when no product was resolved (e.g. quote-to-order) or no mapping is configured.
        return result.fallbackDisplay ? `Estimated shipping: ${result.fallbackDisplay}` : undefined;
    }
    return result.estimatedShipDateDisplay
        ? `Estimated shipping: ${result.estimatedShipDateDisplay}`
        : undefined;
}

/**
 * Resolves the configured base valve's estimated ship date via the configurator's
 * bomLeadTime endpoint, which applies rules 1-5 server-side (unit inventory first,
 * then latest BOM date + buffer, then the "up to X weeks" fallback).
 */
export function useBomLeadTime(options: UseBomLeadTimeOptions): BomLeadTimeState {
    const key = options.skip ? "" : requestKey(options);
    const [state, setState] = useState<BomLeadTimeState>({ loading: !options.skip, error: null });

    // Read through a ref so the changing array/object identities don't re-run
    // (and therefore cancel) the effect on every render.
    const optionsRef = useRef(options);
    optionsRef.current = options;

    useEffect(() => {
        if (!key) {
            setState({ loading: false, error: null });
            return;
        }
        let cancelled = false;
        // Keep the previous result visible while re-fetching (e.g. on a qty change)
        // so consumers don't have to blank the UI.
        setState(s => ({ ...s, loading: true, error: null }));
        fetchDeduped(key, optionsRef.current)
            .then(result => {
                if (cancelled) {
                    return;
                }
                setState({ loading: false, error: null, result, caption: buildLeadTimeCaption(result) });
            })
            .catch((err: unknown) => {
                if (cancelled) {
                    return;
                }
                const message = err instanceof Error ? err.message : "Failed to load lead time";
                setState(s => ({ ...s, loading: false, error: message }));
            });

        return () => {
            cancelled = true;
        };
    }, [key]);

    return state;
}
